/**
 * Canonical deterministic island field shared by the Colyseus server (grounding,
 * spawning, placement authority) and the Three.js client (world streaming,
 * rendering). This is the land twin of `shared/waveField.ts`: a pure, dependency-
 * free module that maps world coordinates to sparse island cells.
 *
 * The world is an infinite hash grid of cells. Most cells are empty; a small
 * fraction (`ISLAND_FIELD.density`) hold exactly one island. Every value below is
 * a pure function of the world seed and the input coordinate, so the server and
 * client derive bit-identical cells, heights and prop placements.
 *
 * Determinism: this module must never use `Math.random`. The lattice hash uses
 * integer mixing only (`Math.imul`, `^`, `>>>`) — specified identically in every
 * V8 build — and the field is CPU-only on both sides, so IEEE-754 double math is
 * identical in browser and Node.
 */

export interface IslandCell {
  /** Stable id `i<gx>_<gz>`, or `home` for the forced cell (0,0). */
  id: string;
  /** World-space centre (cell centre + deterministic jitter). */
  x: number;
  z: number;
  /** Island base radius, metres. */
  radius: number;
  /** Peak terrain height, metres. */
  height: number;
  /** Per-island seed for shape fBm + prop placement. */
  seed: number;
  /** 'rock' | 'isle' | 'home'. 'home'/'isle' get settlement props. */
  kind: 'rock' | 'isle' | 'home';
}

export interface PropPlacement {
  /** Canonical manifest name: 'palm' | 'island-rock' | 'island-rock-b' | ... */
  type: string;
  x: number;
  z: number;
  /** Ground height at (x,z) (= islandFloor when above water). */
  y: number;
  /** Yaw, radians. */
  yaw: number;
  /** Uniform scale. */
  scale: number;
}

/** Frozen world-field tuning. The fixture check pins these + derived fixtures. */
export const ISLAND_FIELD = {
  /** World seed. Frozen; any change shifts every island and fails the fixture check. */
  seed: 0x15144d,
  /** Cell edge length, metres. Islands never span more than one cell. */
  cellSize: 256,
  /** Fraction of cells that hold an island (cell 0,0 is always the home island). */
  density: 0.04,
  /** Island base radius bounds, metres. */
  minRadius: 45,
  maxRadius: 120,
  /** Peak terrain height bounds, metres. */
  minPeak: 6,
  maxPeak: 24,
  /** Base-building grid snap, metres. */
  buildGrid: 2,
} as const;

/**
 * Shoal tuning. The signed floor dips below sea level in a ring just outside the
 * shoreline, giving shallow water for the grounding system for free.
 * `maxInfluence = SHOAL_FACTOR * maxRadius` bounds every island's reach, which is
 * what keeps the height query a small constant number of cell probes.
 */
const SHOAL_FACTOR = 1.4;
const SHOAL_AMPLITUDE = 0.14;

/** Base wavelength (metres) of the lowest fBm octave shaping the island dome. */
const NOISE_WAVELENGTH = 24;
const NOISE_OCTAVES = 4;

/** Cell jitter safety margin (metres) keeping an island fully inside its cell. */
const JITTER_MARGIN = 2;

/** Fraction of non-home islands that get settlement props ('isle' vs 'rock'). */
const ISLE_FRACTION = 0.5;

/** Harbour offset: ship spawns this far past the home shoreline, facing away. */
const HARBOUR_OFFSET = 22;

/** Mix constants that decorrelate derived PRNG streams from the cell seed. */
const PROP_STREAM = 0x9e3779b9;
const CELL_STREAM = 0x85ebca6b;

/** Prop spacing (jittered-grid step) per prop family, metres. */
const PALM_SPACING = 9;
const ROCK_SPACING = 12;
const SETTLEMENT_SPACING = 11;

/** Placement bands (see `islandProps`). */
const ROCK_STEEP_SLOPE = 0.5;
const ROCK_BAND_LOW = 0.03; // normalised height below which the shoreline is rock
const ROCK_BAND_HIGH = 0.6; // normalised height above which the hilltop is rock
const SAND_BAND_HIGH = 0.12; // below this is sand, above is grass (palms)
const SETTLEMENT_SLOPE = 0.3;
const SETTLEMENT_LOW = 0.15;
const SETTLEMENT_HIGH = 0.45;

/** Deterministic mulberry32 PRNG (self-contained; mirrors server `noise.ts`). */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * 2D integer lattice hash → unsigned 32-bit. No `Math.sin`/`cos`, so the result
 * is bit-identical across V8 runtimes and stays GPU-portable if ever needed.
 *
 * The two coordinates are folded in with integer *addition* (not XOR): XOR-only
 * mixing is GF(2)-linear, which leaves a large collision kernel between (gx,gz)
 * pairs; addition's carry propagation breaks that and keeps cells distinct.
 */
function hash2Int(gx: number, gz: number, seed: number): number {
  let h = seed >>> 0;
  h = (h + Math.imul(gx >>> 0, 0x27d4eb2d)) >>> 0;
  h = (h + Math.imul(gz >>> 0, 0x165667b1)) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** 2D lattice hash → [0,1). */
function hash2(gx: number, gz: number, seed: number): number {
  return hash2Int(gx, gz, seed) / 4294967296;
}

/** Quintic fade (same as `createPerlin1D` in server `noise.ts`). */
function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** 2D value noise in roughly [0,1). */
function valueNoise2(x: number, z: number, seed: number): number {
  const x0 = Math.floor(x);
  const z0 = Math.floor(z);
  const tx = fade(x - x0);
  const tz = fade(z - z0);
  const v00 = hash2(x0, z0, seed);
  const v10 = hash2(x0 + 1, z0, seed);
  const v01 = hash2(x0, z0 + 1, seed);
  const v11 = hash2(x0 + 1, z0 + 1, seed);
  const vx0 = lerp(v00, v10, tx);
  const vx1 = lerp(v01, v11, tx);
  return lerp(vx0, vx1, tz);
}

/** 2D value-noise fBm, normalised to [0,1). */
function fbm(x: number, z: number, seed: number, octaves = NOISE_OCTAVES): number {
  let sum = 0;
  let amplitude = 1;
  let frequency = 1;
  let total = 0;
  for (let i = 0; i < octaves; i += 1) {
    sum += amplitude * valueNoise2(x * frequency, z * frequency, seed + i * 1013);
    total += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }
  return sum / total;
}

/**
 * Unitless radial falloff for one island at normalised distance `u = d / radius`.
 * `u < 1` is the land dome (1 at centre, 0 at the shoreline); `1 <= u <
 * SHOAL_FACTOR` is the submerged shoal ring (0 → -SHOAL_AMPLITUDE → 0); beyond
 * that it is exactly 0 (open water). Continuous at the shoreline and at the outer
 * shoal edge, so the signed floor is continuous everywhere.
 */
function radialFalloff(u: number): number {
  if (u <= 0) return 1;
  if (u < 1) return Math.cos(u * (Math.PI / 2));
  if (u < SHOAL_FACTOR) {
    const s = (u - 1) / (SHOAL_FACTOR - 1);
    return -SHOAL_AMPLITUDE * Math.sin(s * Math.PI);
  }
  return 0;
}

/** Compute one island cell from its grid coordinates, or null for empty cells. */
function islandForCell(gx: number, gz: number): IslandCell | null {
  const home = gx === 0 && gz === 0;
  if (!home && hash2(gx, gz, ISLAND_FIELD.seed) >= ISLAND_FIELD.density) return null;

  const cellSeed = hash2Int(gx, gz, ISLAND_FIELD.seed);
  const rng = mulberry32(cellSeed ^ CELL_STREAM);

  const radius = lerp(ISLAND_FIELD.minRadius, ISLAND_FIELD.maxRadius, rng());
  const height = lerp(ISLAND_FIELD.minPeak, ISLAND_FIELD.maxPeak, rng());
  const jitterX = rng() * 2 - 1;
  const jitterZ = rng() * 2 - 1;
  const kind = home ? 'home' : rng() < ISLE_FRACTION ? 'isle' : 'rock';

  // Keep the island fully inside its own cell so `islandAt` and the radius query
  // only ever need a ±1-cell neighbourhood.
  const range = ISLAND_FIELD.cellSize / 2 - radius - JITTER_MARGIN;
  const x = (gx + 0.5) * ISLAND_FIELD.cellSize + jitterX * range;
  const z = (gz + 0.5) * ISLAND_FIELD.cellSize + jitterZ * range;

  return {
    id: home ? 'home' : `i${gx}_${gz}`,
    x,
    z,
    radius,
    height,
    seed: cellSeed,
    kind,
  };
}

/**
 * Island cells whose bounding circle intersects the query circle (x,z,r).
 * Empty far from land. Sorted by distance (ties broken by id) for deterministic
 * streaming order. Complexity: O(ceil((r + maxRadius)/cellSize)²) cell probes —
 * a small constant for the radii the client and server query.
 */
export function islandsInRadius(x: number, z: number, r: number): IslandCell[] {
  const reach = r + ISLAND_FIELD.maxRadius;
  const gx0 = Math.floor((x - reach) / ISLAND_FIELD.cellSize);
  const gx1 = Math.floor((x + reach) / ISLAND_FIELD.cellSize);
  const gz0 = Math.floor((z - reach) / ISLAND_FIELD.cellSize);
  const gz1 = Math.floor((z + reach) / ISLAND_FIELD.cellSize);

  const results: IslandCell[] = [];
  for (let gx = gx0; gx <= gx1; gx += 1) {
    for (let gz = gz0; gz <= gz1; gz += 1) {
      const cell = islandForCell(gx, gz);
      if (!cell) continue;
      if (Math.hypot(x - cell.x, z - cell.z) <= r + cell.radius) results.push(cell);
    }
  }

  results.sort((a, b) => {
    const da = Math.hypot(x - a.x, z - a.z);
    const db = Math.hypot(x - b.x, z - b.z);
    return da - db || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  });
  return results;
}

/**
 * The island cell containing (x,z), else null. Complexity: O(1) (one cell hash).
 */
export function islandAt(x: number, z: number): IslandCell | null {
  return islandForCell(
    Math.floor(x / ISLAND_FIELD.cellSize),
    Math.floor(z / ISLAND_FIELD.cellSize),
  );
}

/** Maximum distance an island can influence the signed floor. */
const MAX_INFLUENCE = SHOAL_FACTOR * ISLAND_FIELD.maxRadius;

/**
 * Signed island terrain at (x,z). 0 = open water (no island influence),
 * >0 = land above sea level, <0 = submerged shoal/sandbar.
 *
 * Sums the falloff of every island whose influence reaches the point. Because an
 * island is contained in its cell and influence is bounded by `MAX_INFLUENCE`,
 * only a fixed ±1-cell neighbourhood can contribute — O(1) per query.
 */
export function islandFloor(x: number, z: number): number {
  const gx0 = Math.floor((x - MAX_INFLUENCE) / ISLAND_FIELD.cellSize);
  const gx1 = Math.floor((x + MAX_INFLUENCE) / ISLAND_FIELD.cellSize);
  const gz0 = Math.floor((z - MAX_INFLUENCE) / ISLAND_FIELD.cellSize);
  const gz1 = Math.floor((z + MAX_INFLUENCE) / ISLAND_FIELD.cellSize);

  let floor = 0;
  for (let gx = gx0; gx <= gx1; gx += 1) {
    for (let gz = gz0; gz <= gz1; gz += 1) {
      const cell = islandForCell(gx, gz);
      if (!cell) continue;
      const d = Math.hypot(x - cell.x, z - cell.z);
      const falloff = radialFalloff(d / cell.radius);
      if (falloff === 0) continue;
      const shape = 0.6 + 0.4 * fbm(x / NOISE_WAVELENGTH, z / NOISE_WAVELENGTH, cell.seed);
      floor += cell.height * falloff * shape;
    }
  }
  return floor;
}

/** Finite-difference step for the slope query, metres. */
const SLOPE_STEP = 1;

/**
 * Terrain slope magnitude at (x,z) via central finite differences. Used by the
 * placement systems to reject cliffs and steep shores. O(1) (four `islandFloor`
 * probes).
 */
export function islandSlope(x: number, z: number): number {
  const dx = (islandFloor(x + SLOPE_STEP, z) - islandFloor(x - SLOPE_STEP, z)) / (2 * SLOPE_STEP);
  const dz = (islandFloor(x, z + SLOPE_STEP) - islandFloor(x, z - SLOPE_STEP)) / (2 * SLOPE_STEP);
  return Math.hypot(dx, dz);
}

const ROCK_TYPES = ['island-rock', 'island-rock-b', 'island-rock-c'] as const;
const SETTLEMENT_TYPES = ['crate', 'barrel', 'chest'] as const;

interface PropEmitter {
  (x: number, z: number): void;
}

/** Emit a jittered grid of samples over the island's bounding square. */
function jitteredGrid(
  rng: () => number,
  minX: number,
  minZ: number,
  maxX: number,
  maxZ: number,
  step: number,
  emit: PropEmitter,
): void {
  const startX = Math.ceil(minX / step) * step;
  const startZ = Math.ceil(minZ / step) * step;
  for (let x = startX; x <= maxX; x += step) {
    for (let z = startZ; z <= maxZ; z += step) {
      emit(x + (rng() * 2 - 1) * (step * 0.5), z + (rng() * 2 - 1) * (step * 0.5));
    }
  }
}

/**
 * Deterministic natural-prop placements for one island (pure; both sides derive
 * the identical list). Rocks hug the shoreline and hilltop, palms cover sand and
 * grass, and 'home'/'isle' islands add a small settlement of crates/barrels/
 * chests on flat grass near the centre.
 */
export function islandProps(cell: IslandCell): PropPlacement[] {
  const rng = mulberry32(cell.seed ^ PROP_STREAM);
  const placements: PropPlacement[] = [];
  const half = cell.radius;
  const minX = cell.x - half;
  const minZ = cell.z - half;
  const maxX = cell.x + half;
  const maxZ = cell.z + half;

  const push = (type: string, x: number, z: number, scaleRange: readonly [number, number]): void => {
    const y = islandFloor(x, z);
    if (y <= 0.02) return; // never below/at the waterline
    placements.push({
      type,
      x,
      z,
      y,
      yaw: rng() * Math.PI * 2,
      scale: lerp(scaleRange[0], scaleRange[1], rng()),
    });
  };

  // Rocks: shoreline + steep slopes + rocky hilltops.
  jitteredGrid(rng, minX, minZ, maxX, maxZ, ROCK_SPACING, (x, z) => {
    const nh = islandFloor(x, z) / cell.height;
    if (nh <= ROCK_BAND_LOW || nh >= ROCK_BAND_HIGH || islandSlope(x, z) > ROCK_STEEP_SLOPE) {
      const type = ROCK_TYPES[Math.floor(rng() * ROCK_TYPES.length)];
      push(type, x, z, [0.8, 1.5]);
    }
  });

  // Palms: sand and grass between the shoreline and the rocky hilltop.
  jitteredGrid(rng, minX, minZ, maxX, maxZ, PALM_SPACING, (x, z) => {
    const floor = islandFloor(x, z);
    const nh = floor / cell.height;
    if (nh > ROCK_BAND_LOW && nh < ROCK_BAND_HIGH && islandSlope(x, z) <= ROCK_STEEP_SLOPE) {
      // Sand (low band) gets sparse palms; grass gets the fuller cover.
      const sand = nh <= SAND_BAND_HIGH;
      if (rng() < (sand ? 0.35 : 0.6)) push('palm', x, z, [0.85, 1.35]);
    }
  });

  // Settlement: flat grass near the centre, only on habitable islands.
  if (cell.kind !== 'rock') {
    jitteredGrid(rng, minX, minZ, maxX, maxZ, SETTLEMENT_SPACING, (x, z) => {
      const floor = islandFloor(x, z);
      const nh = floor / cell.height;
      if (nh < SETTLEMENT_LOW || nh > SETTLEMENT_HIGH) return;
      if (islandSlope(x, z) > SETTLEMENT_SLOPE) return;
      if (rng() < 0.5) return; // keep settlements sparse
      const type = SETTLEMENT_TYPES[Math.floor(rng() * SETTLEMENT_TYPES.length)];
      push(type, x, z, [0.9, 1.2]);
    });
  }

  return placements;
}

/**
 * Deterministic harbour pose beside the home island: just past the home
 * shoreline due south (+z), facing further south into open water. The yaw of
 * -π/2 maps to a +z velocity under the room's `vx = cos(yaw)·speed,
 * vz = -sin(yaw)·speed` convention.
 */
export function harbourSpawn(): { x: number; z: number; yaw: number } {
  const home = islandForCell(0, 0) as IslandCell;
  return {
    x: home.x,
    z: home.z + home.radius + HARBOUR_OFFSET,
    yaw: -Math.PI / 2,
  };
}
